import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, TruckIcon, Warehouse, AlertTriangle, Lock } from 'lucide-react';
import { toast } from 'sonner';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, checkLoginStatus } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginStatus, setLoginStatus] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  // Check login status when username changes
  useEffect(() => {
    const checkStatus = async () => {
      if (formData.username.length >= 3) {
        const status = await checkLoginStatus(formData.username);
        setLoginStatus(status);
      } else {
        setLoginStatus(null);
      }
    };
    
    const timeoutId = setTimeout(checkStatus, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.username, checkLoginStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if account is locked
    if (loginStatus?.locked) {
      toast.error(`Compte verrouillé. Réessayez dans ${loginStatus.remaining_minutes} minutes.`);
      return;
    }
    
    setLoading(true);

    const result = await login(formData.username, formData.password);
    
    if (result.success) {
      toast.success('Connexion réussie! Session valide pour 24 heures.');
      navigate('/dashboard');
    } else {
      if (result.locked) {
        toast.error(result.error);
        // Refresh login status
        const status = await checkLoginStatus(formData.username);
        setLoginStatus(status);
      } else {
        toast.error(result.error);
        // Refresh login status to show remaining attempts
        const status = await checkLoginStatus(formData.username);
        setLoginStatus(status);
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 relative bg-gradient-to-br from-blue-600 via-violet-600 to-violet-800"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-violet-900/40"></div>
        <div className="relative z-10 h-full flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <img 
                src="/logo.png" 
                alt="ECOM GUINEE" 
                className="w-20 h-20 rounded-xl shadow-lg object-cover"
              />
              <div>
                <h1 className="text-3xl font-heading font-black">ECOM GUINEE</h1>
                <p className="text-sm text-violet-200">Gestion automatisée de stock et livraison</p>
              </div>
            </div>
            <h2 className="text-4xl font-heading font-bold mb-4 leading-tight">
              Pilotez votre e-commerce en toute simplicité
            </h2>
            <p className="text-lg text-violet-200">
              Gestion multi-zones, commissions automatiques et suivi en temps réel de vos livraisons.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-6 mt-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <TruckIcon className="h-8 w-8 mb-3 text-violet-200" />
              <p className="text-2xl font-bold mb-1">Multi-zones</p>
              <p className="text-sm text-violet-200">Conakry, Intérieur, Extérieur</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <Warehouse className="h-8 w-8 mb-3 text-yellow-300" />
              <p className="text-2xl font-bold mb-1">Multi-dépôts</p>
              <p className="text-sm text-yellow-200">Gestion intelligente du stock</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center lg:hidden">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-violet-600 to-blue-500 rounded-xl flex items-center justify-center">
                <Warehouse className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-black text-slate-900">EcomTrack Pro</h1>
                <p className="text-xs text-slate-500">Gestion & Livraison</p>
              </div>
            </div>
          </div>

          <Card className="border-slate-200 shadow-xl" data-testid="login-card">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-heading font-bold">Connexion</CardTitle>
              <CardDescription>Accédez à votre espace de gestion</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Account locked warning */}
              {loginStatus?.locked && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <Lock className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Compte temporairement verrouillé</p>
                    <p className="text-xs text-red-600">
                      Trop de tentatives échouées. Réessayez dans {loginStatus.remaining_minutes} minutes.
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Nom d'utilisateur</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="Entrez votre nom d'utilisateur"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    required
                    data-testid="username-input"
                    disabled={loginStatus?.locked}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Entrez votre mot de passe"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    data-testid="password-input"
                    disabled={loginStatus?.locked}
                  />
                </div>

                {/* Show remaining attempts */}
                {loginStatus && !loginStatus.locked && loginStatus.remaining_attempts < 5 && (
                  <div className="flex items-center gap-2 text-sm">
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                    <span className="text-orange-600">
                      {loginStatus.remaining_attempts} tentative(s) restante(s)
                    </span>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-violet-800 hover:bg-violet-900"
                  disabled={loading || loginStatus?.locked}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Connexion...
                    </>
                  ) : loginStatus?.locked ? (
                    <>
                      <Lock className="mr-2 h-4 w-4" />
                      Compte verrouillé
                    </>
                  ) : (
                    'Se connecter'
                  )}
                </Button>
              </form>

              {/* Session info */}
              <div className="mt-4 text-center">
                <Badge variant="outline" className="text-xs">
                  Session valide 24h • Max 5 tentatives
                </Badge>
              </div>

              <div className="mt-6 p-4 bg-violet-50 rounded-lg border border-violet-200">
                <p className="text-xs font-medium text-violet-900 mb-2">Comptes de démonstration :</p>
                <div className="space-y-1 text-xs text-violet-700">
                  <p><strong>Admin:</strong> admin / admin123</p>
                  <p><strong>Manager:</strong> manager1 / manager123</p>
                  <p><strong>Coord. Général:</strong> coord_general / cg123</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
